import React, { useEffect, useState } from 'react';
import { Button, Modal, Typography } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import {
  FIRST_VISIT_STORAGE_KEY,
  markFirstVisitShown,
} from './firstVisitStorage';
import { ANNOUNCE_VERSION_KEY, markAnnouncementSeen } from './announcementStorage';
import './OverseasRedirectModal.css';

const { Text } = Typography;

const ENGLISH_COPY = {
  badge: 'International version available',
  title: 'This site is currently available in Chinese only',
  description: 'Would you like to use 550W Video Eraser instead? It is our international site for removing subtitles and watermarks from videos.',
  confirm: 'Go to 550W Video Eraser',
  stay: 'Use Chinese version',
};

// Keep this list aligned with Web-Overseas/eraser/src/i18n/locales.js.
const COPY = {
  ar: { badge: 'الإصدار الدولي متاح', title: 'هذا الموقع متاح حاليًا باللغة الصينية فقط', description: 'هل ترغب في استخدام 550W Video Eraser؟ إنه موقعنا الدولي لإزالة الترجمة والعلامات المائية من مقاطع الفيديو.', confirm: 'الانتقال إلى 550W Video Eraser', stay: 'استخدام النسخة الصينية' },
  de: { badge: 'Internationale Version verfügbar', title: 'Diese Website ist derzeit nur auf Chinesisch verfügbar', description: 'Möchtest du stattdessen 550W Video Eraser verwenden? Das ist unsere internationale Website zum Entfernen von Untertiteln und Wasserzeichen aus Videos.', confirm: 'Zu 550W Video Eraser', stay: 'Chinesische Version verwenden' },
  en: ENGLISH_COPY,
  es: { badge: 'Versión internacional disponible', title: 'Este sitio web actualmente solo está disponible en chino', description: '¿Quieres usar 550W Video Eraser? Es nuestro sitio internacional para eliminar subtítulos y marcas de agua de videos.', confirm: 'Ir a 550W Video Eraser', stay: 'Usar versión en chino' },
  'es-MX': { badge: 'Versión internacional disponible', title: 'Este sitio web actualmente solo está disponible en chino', description: '¿Quieres usar 550W Video Eraser? Es nuestro sitio internacional para eliminar subtítulos y marcas de agua de videos.', confirm: 'Ir a 550W Video Eraser', stay: 'Usar versión en chino' },
  fr: { badge: 'Version internationale disponible', title: 'Ce site est actuellement disponible uniquement en chinois', description: 'Souhaitez-vous utiliser 550W Video Eraser ? C’est notre site international pour supprimer les sous-titres et les filigranes des vidéos.', confirm: 'Accéder à 550W Video Eraser', stay: 'Utiliser la version chinoise' },
  'fr-CA': { badge: 'Version internationale offerte', title: 'Ce site est actuellement offert seulement en chinois', description: 'Souhaitez-vous utiliser 550W Video Eraser? C’est notre site international pour supprimer les sous-titres et les filigranes des vidéos.', confirm: 'Aller à 550W Video Eraser', stay: 'Utiliser la version chinoise' },
  he: { badge: 'גרסה בינלאומית זמינה', title: 'האתר הזה זמין כרגע בסינית בלבד', description: 'האם תרצה להשתמש ב-550W Video Eraser? זהו האתר הבינלאומי שלנו להסרת כתוביות וסימני מים מסרטונים.', confirm: 'מעבר ל-550W Video Eraser', stay: 'שימוש בגרסה הסינית' },
  id: { badge: 'Versi internasional tersedia', title: 'Situs ini saat ini hanya tersedia dalam bahasa Mandarin', description: 'Ingin menggunakan 550W Video Eraser? Ini adalah situs internasional kami untuk menghapus subtitle dan watermark dari video.', confirm: 'Buka 550W Video Eraser', stay: 'Gunakan versi bahasa Mandarin' },
  it: { badge: 'Versione internazionale disponibile', title: 'Questo sito è attualmente disponibile solo in cinese', description: 'Vuoi usare 550W Video Eraser? È il nostro sito internazionale per rimuovere sottotitoli e filigrane dai video.', confirm: 'Vai a 550W Video Eraser', stay: 'Usa la versione cinese' },
  ja: { badge: '国際版をご利用いただけます', title: 'このサイトは現在、中国語版のみです', description: '550W Video Eraser を使用しますか？動画の字幕やウォーターマークを削除できる国際版サイトです。', confirm: '550W Video Eraser を開く', stay: '中国語版を使用する' },
  ko: { badge: '국제 버전을 이용할 수 있습니다', title: '이 사이트는 현재 중국어로만 제공됩니다', description: '550W Video Eraser를 사용하시겠습니까? 동영상의 자막과 워터마크를 제거할 수 있는 국제 버전 사이트입니다.', confirm: '550W Video Eraser로 이동', stay: '중국어 버전 사용' },
  ms: { badge: 'Versi antarabangsa tersedia', title: 'Laman ini kini hanya tersedia dalam bahasa Cina', description: 'Adakah anda mahu menggunakan 550W Video Eraser? Ini ialah laman antarabangsa kami untuk membuang sari kata dan tera air daripada video.', confirm: 'Buka 550W Video Eraser', stay: 'Gunakan versi bahasa Cina' },
  nl: { badge: 'Internationale versie beschikbaar', title: 'Deze website is momenteel alleen in het Chinees beschikbaar', description: 'Wil je 550W Video Eraser gebruiken? Dit is onze internationale website voor het verwijderen van ondertitels en watermerken uit video’s.', confirm: 'Naar 550W Video Eraser', stay: 'Chinese versie gebruiken' },
  pl: { badge: 'Wersja międzynarodowa jest dostępna', title: 'Ta strona jest obecnie dostępna tylko po chińsku', description: 'Czy chcesz użyć 550W Video Eraser? To nasza międzynarodowa strona do usuwania napisów i znaków wodnych z filmów.', confirm: 'Przejdź do 550W Video Eraser', stay: 'Użyj wersji chińskiej' },
  'pt-BR': { badge: 'Versão internacional disponível', title: 'Este site está disponível atualmente apenas em chinês', description: 'Deseja usar o 550W Video Eraser? Este é nosso site internacional para remover legendas e marcas-d’água de vídeos.', confirm: 'Ir para 550W Video Eraser', stay: 'Usar versão em chinês' },
  'pt-PT': { badge: 'Versão internacional disponível', title: 'Este site está atualmente disponível apenas em chinês', description: 'Pretende utilizar o 550W Video Eraser? Este é o nosso site internacional para remover legendas e marcas de água de vídeos.', confirm: 'Ir para 550W Video Eraser', stay: 'Utilizar a versão chinesa' },
  ru: { badge: 'Международная версия доступна', title: 'Этот сайт сейчас доступен только на китайском языке', description: 'Хотите использовать 550W Video Eraser? Это наш международный сайт для удаления субтитров и водяных знаков из видео.', confirm: 'Перейти в 550W Video Eraser', stay: 'Использовать китайскую версию' },
  th: { badge: 'มีเวอร์ชันสากลให้ใช้', title: 'เว็บไซต์นี้ให้บริการเป็นภาษาจีนเท่านั้นในขณะนี้', description: 'ต้องการใช้ 550W Video Eraser หรือไม่? นี่คือเว็บไซต์สากลของเราสำหรับลบคำบรรยายและลายน้ำออกจากวิดีโอ', confirm: 'ไปที่ 550W Video Eraser', stay: 'ใช้เวอร์ชันภาษาจีน' },
  tr: { badge: 'Uluslararası sürüm mevcut', title: 'Bu site şu anda yalnızca Çince olarak sunulmaktadır', description: '550W Video Eraser kullanmak ister misiniz? Bu, videolardaki altyazıları ve filigranları kaldırmak için uluslararası sitemizdir.', confirm: '550W Video Eraser’a git', stay: 'Çince sürümü kullan' },
  uk: { badge: 'Міжнародна версія доступна', title: 'Цей сайт наразі доступний лише китайською мовою', description: 'Бажаєте скористатися 550W Video Eraser? Це наш міжнародний сайт для видалення субтитрів і водяних знаків із відео.', confirm: 'Перейти до 550W Video Eraser', stay: 'Використовувати китайську версію' },
  vi: { badge: 'Đã có phiên bản quốc tế', title: 'Trang web này hiện chỉ có tiếng Trung', description: 'Bạn có muốn dùng 550W Video Eraser không? Đây là trang quốc tế của chúng tôi để xóa phụ đề và hình mờ khỏi video.', confirm: 'Mở 550W Video Eraser', stay: 'Dùng phiên bản tiếng Trung' },
  'zh-Hant': { badge: '國際版現已推出', title: '此網站目前僅提供簡體中文版', description: '要改用 550W Video Eraser 嗎？這是我們的國際版網站，可移除影片中的字幕與浮水印。', confirm: '前往 550W Video Eraser', stay: '使用簡體中文版' },
};

const LOCALE_ALIASES = {
  'zh': 'zh-Hans', 'zh-cn': 'zh-Hans', 'zh-sg': 'zh-Hans', 'zh-hans': 'zh-Hans',
  'zh-tw': 'zh-Hant', 'zh-hk': 'zh-Hant', 'zh-mo': 'zh-Hant',
  'pt': 'pt-BR',
  'es-419': 'es-MX',
};

function resolveLocale(language) {
  if (!language || typeof language !== 'string') return 'en';
  const normalized = language.trim().replace(/_/g, '-').toLowerCase();
  const supported = Object.keys(COPY).find((locale) => locale.toLowerCase() === normalized);
  if (supported) return supported;
  if (LOCALE_ALIASES[normalized]) return LOCALE_ALIASES[normalized];
  const base = normalized.split('-')[0];
  if (base === 'zh') return 'zh-Hans';
  return Object.keys(COPY).find((locale) => locale.toLowerCase().split('-')[0] === base) || 'en';
}

function getBrowserLocale() {
  const languages = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language];
  return resolveLocale(languages.find(Boolean));
}

// These are existing qzm keys. Seeing either means this browser has already
// interacted with the Chinese site, so no international redirect prompt is needed.
function isFirstVisit() {
  try {
    return localStorage.getItem(FIRST_VISIT_STORAGE_KEY) !== 'true'
      && localStorage.getItem(ANNOUNCE_VERSION_KEY) === null;
  } catch (e) {
    return false;
  }
}

export function shouldShowOverseasRedirect() {
  const browserLocale = getBrowserLocale();
  return browserLocale !== 'zh-Hans' && isFirstVisit();
}

function OverseasRedirectModal() {
  const [locale, setLocale] = useState(null);

  useEffect(() => {
    const browserLocale = getBrowserLocale();
    if (shouldShowOverseasRedirect()) {
      setLocale(browserLocale);
    }
  }, []);

  const handleStay = () => {
    markFirstVisitShown();
    // Avoid immediately stacking the existing announcement modal after this choice.
    markAnnouncementSeen();
    setLocale(null);
  };

  const handleRedirect = () => {
    // 未选择中文版时不写入访问标记，之后回到本网站仍可再次看到引导。
    window.location.assign('https://eraser.550wai.com');
  };

  if (!locale) return null;
  const copy = COPY[locale] || ENGLISH_COPY;
  const isRtl = locale === 'ar' || locale === 'he';

  return (
    <Modal
      open
      footer={null}
      centered
      width={500}
      closable={false}
      maskClosable={false}
      keyboard={false}
      zIndex={2000}
      className="overseas-redirect-modal"
      destroyOnHidden
    >
      <div className="overseas-redirect-body" dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="overseas-redirect-icon"><GlobalOutlined /></div>
        <div className="overseas-redirect-badge">{copy.badge}</div>
        <div className="overseas-redirect-title">{copy.title}</div>
        <Text type="secondary" className="overseas-redirect-description">{copy.description}</Text>
        <div className="overseas-redirect-actions">
          <Button className="overseas-redirect-stay" onClick={handleStay}>{copy.stay}</Button>
          <Button type="primary" className="overseas-redirect-confirm" onClick={handleRedirect}>{copy.confirm}</Button>
        </div>
      </div>
    </Modal>
  );
}

export default OverseasRedirectModal;
